/**
 * Word Comparison Utilities
 * Extracted from DictationPage for reuse across cloze, dictation, and other exercises
 */

import { getSharedLemma, getLemmaRelationInfo } from './lemmatizer';

/**
 * Normalize a word for comparison
 * - Remove diacritics (é → e, ñ → n, etc.)
 * - Remove leading/trailing quotes and punctuation
 * - Preserve abbreviations like "p.m.", "a.m."
 * - Case-sensitive comparison (no toLowerCase)
 */
export const normalizeWord = (word: string): string => {
  if (!word) return '';

  const normalized = word
    .normalize('NFD')                    // Decompose accented characters (é → e + ́)
    .replace(/[\u0300-\u036f]/g, '')     // Remove diacritical marks
    .replace(/^[^\w']+/, '')             // Remove leading punctuation (including quotes)
    .replace(/[""'']+$/, '');            // Remove trailing quotes (smart and straight)

  // Check if word is an abbreviation pattern like "a.m." or "p.m."
  const abbrevPattern = /^[a-zA-Z](\.[a-zA-Z])+\.?[!?,;:]*$/;

  if (abbrevPattern.test(normalized)) {
    // For abbreviations, only remove non-period trailing punctuation
    return normalized.replace(/[!?,;:]+$/, '');
  } else {
    // For regular words, remove all trailing punctuation including periods and hyphens
    return normalized.replace(/[.!?,;:-]+$/, '');
  }
};

/**
 * Coerce the first character's case to match the target word.
 * Neutralises Android keyboards (Gboard/Samsung) that autocapitalise the
 * first letter on focus regardless of `autoCapitalize="none"`.
 * Returns the input unchanged if there is no target first-letter case hint.
 */
export const matchFirstCharCase = (input: string, correctWord: string): string => {
  if (!input || !correctWord) return input;
  const targetFirst = normalizeWord(correctWord)[0];
  const userFirst = input[0];
  if (!targetFirst || !userFirst) return input;
  if (!/[A-Za-z]/.test(targetFirst) || !/[A-Za-z]/.test(userFirst)) return input;
  const targetIsUpper = targetFirst === targetFirst.toUpperCase() && targetFirst !== targetFirst.toLowerCase();
  const userIsUpper = userFirst === userFirst.toUpperCase() && userFirst !== userFirst.toLowerCase();
  if (targetIsUpper === userIsUpper) return input;
  const coerced = targetIsUpper ? userFirst.toUpperCase() : userFirst.toLowerCase();
  return coerced + input.slice(1);
};

/**
 * Coerce the entire input's case to follow the target word's case pattern.
 * Defends against aggressive Android IMEs (e.g. Huawei Browser's keyboard)
 * that capitalise multiple characters even when `autoCapitalize="none"` is set.
 *
 * Rules (based on the common case patterns of dictation targets):
 * - target all-lowercase → force input to lowercase
 * - target TitleCase (Upper + rest lower) → force TitleCase
 * - any other pattern (e.g. "iPhone", "macOS") → only coerce the first char
 *   so we don't silently "fix" real capitalisation mistakes the learner made.
 */
export const coerceInputCase = (input: string, correctWord: string): string => {
  if (!input || !correctWord) return input;
  const normalizedTarget = normalizeWord(correctWord);
  if (!normalizedTarget) return input;

  const first = normalizedTarget[0];
  const rest = normalizedTarget.slice(1);
  const firstIsUpper = /[A-Z]/.test(first);
  const restHasUpper = /[A-Z]/.test(rest);
  const restHasLower = /[a-z]/.test(rest);

  if (!firstIsUpper && !restHasUpper) {
    return input.toLowerCase();
  }
  if (firstIsUpper && !restHasUpper && restHasLower) {
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
  }
  // Complex / mixed-case target — only coerce the first character so the
  // learner still has to type the correct internal casing themselves.
  return matchFirstCharCase(input, correctWord);
};

/**
 * Check if user input matches correct word (case-sensitive)
 */
export const isWordCorrect = (inputWord: string, correctWord: string): boolean => {
  if (!inputWord || !correctWord) return false;
  return normalizeWord(inputWord) === normalizeWord(correctWord);
};

/**
 * Check if user input matches correct word (case-insensitive, for cloze)
 */
export const isWordCorrectIgnoreCase = (inputWord: string, correctWord: string): boolean => {
  if (!inputWord || !correctWord) return false;
  return normalizeWord(inputWord).toLowerCase() === normalizeWord(correctWord).toLowerCase();
};

/**
 * Word comparison result with detailed analysis
 */
export interface WordComparisonResult {
  isCorrect: boolean;
  userInput: string;
  correctWord: string;
  normalizedUser: string;
  normalizedCorrect: string;
  // Lemma analysis (e.g., "cook" vs "cooks")
  hasLemmaRelation: boolean;
  lemmaExplanation?: {
    explanation: string;
    userForm?: string;
    correctForm?: string;
    sharedLemma?: string;
  };
  // Capitalization issue (e.g., "midas" vs "Midas")
  isCapitalizationError: boolean;
}

/**
 * Compare user input with correct word and provide detailed analysis
 */
export const compareWord = (
  userInput: string,
  correctWord: string,
  t?: (key: string, options?: any) => string,
  wordRole?: string,
  wordForm?: string,
): WordComparisonResult => {
  const normalizedUser = normalizeWord(userInput);
  const normalizedCorrect = normalizeWord(correctWord);
  const isCorrect = normalizedUser === normalizedCorrect;

  const result: WordComparisonResult = {
    isCorrect,
    userInput,
    correctWord,
    normalizedUser,
    normalizedCorrect,
    hasLemmaRelation: false,
    isCapitalizationError: false,
  };

  if (isCorrect) return result;

  // Check if it's only a capitalization difference
  if (normalizedUser.toLowerCase() === normalizedCorrect.toLowerCase()) {
    result.isCapitalizationError = true;
    return result;
  }

  // Check for lemma relationship (e.g., "cook" vs "cooks", "run" vs "ran")
  const sharedLemma = getSharedLemma(normalizedUser, normalizedCorrect);
  if (sharedLemma) {
    result.hasLemmaRelation = true;
    const info = getLemmaRelationInfo(normalizedUser, normalizedCorrect, sharedLemma, wordRole, wordForm);

    const explanation = t
      ? t(`dictation.lemmaHint.${info.translationKey}`, {
          defaultValue: `${info.userWord} ≠ ${info.correctWord}`,
          userWord: info.userWord,
          correctWord: info.correctWord,
          lemma: info.lemma,
        })
      : `${info.userWord} ≠ ${info.correctWord}`;

    result.lemmaExplanation = {
      explanation,
      userForm: info.userForm,
      correctForm: info.correctForm,
      sharedLemma,
    };
  }

  return result;
};

/**
 * Check if a string contains full-width alphanumeric characters (Ａ-Ｚ, ａ-ｚ, ０-９).
 * These are commonly typed when the user's IME is in full-width mode.
 */
export const containsFullWidthAlpha = (text: string): boolean => {
  // Full-width digits: U+FF10-FF19, uppercase: U+FF21-FF3A, lowercase: U+FF41-FF5A
  return /[\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A]/.test(text);
};

/**
 * Convert full-width alphanumeric characters to half-width equivalents.
 */
export const toHalfWidth = (text: string): string => {
  return text.replace(/[\uFF10-\uFF5A]/g, (ch) => {
    return String.fromCharCode(ch.charCodeAt(0) - 0xFEE0);
  });
};

/**
 * Check if a token is punctuation-only (no typeable characters like "…", "—", "–")
 */
export const isPunctuationOnly = (word: string): boolean => {
  return !/[a-zA-Z0-9]/.test(word);
};

/**
 * Strip leading + trailing punctuation but keep internal apostrophes
 * and hyphens — so "anecdotes." → "anecdotes", "ten." → "ten",
 * "(running)" → "running", but "I'd", "well-known", "won't" all stay
 * intact. Used by the speaking-assessment surfaces when displaying
 * a word to the learner (panel chip, retry modal headline, retry
 * reference text). The underlying diff data keeps the raw token —
 * we only clean the display + the per-word ASR reference.
 */
export const stripWordPunctuation = (word: string): string => {
  return word.replace(/^[^A-Za-z0-9'\-]+|[^A-Za-z0-9'\-]+$/g, '');
};

/**
 * Get max length for word input (based on normalized length)
 */
export const getWordMaxLength = (word: string): number => {
  return normalizeWord(word).length;
};

/**
 * Extract leading punctuation from a word (to display before input)
 * Matches any leading non-word characters: quotes, currency symbols ($€£¥), etc.
 */
export const getLeadingPunctuation = (word: string): string => {
  const match = word.match(/^[^\w']+/);
  return match ? match[0] : '';
};

/**
 * Extract trailing punctuation from a word (to display after input)
 */
export const getTrailingPunctuation = (word: string): string => {
  const abbrevPattern = /^[a-zA-Z](\.[a-zA-Z])+\.?[!?,;:]*$/;
  if (abbrevPattern.test(word)) {
    const match = word.match(/[!?,;:]+$/);
    return match ? match[0] : '';
  }
  const match = word.match(/[.!?,;:\-""''「」『』)\]}>]+$/);
  return match ? match[0] : '';
};
